import { Checkbox, Col, Form, InputNumber, Row, Tabs } from 'antd';
import { TitlesTypes } from '../../../constants/constants';
import { useStore } from '../../../store/store';
import TitleConfiguration from '../../common/TitleConfiguration';
import { modalFieldLayout } from './fieldLayout';
import { useCallback, useMemo } from 'react';

const selector = ({
  configuration,
  setConfiguration,
  summaryConfiguration,
  setSummaryConfiguration,
  paysageMode,
  setPaysageMode,
}) => ({
  configuration,
  setConfiguration,
  summaryConfiguration,
  setSummaryConfiguration,
  paysageMode,
  setPaysageMode,
});



export const TabsSummary = () => {
  const {
    configuration,
    setConfiguration,
    summaryConfiguration,
    setSummaryConfiguration,
  } = useStore(selector);

  const styleRuleChangeObject = useCallback((options = {}) => {
    return {
      setFunction: options.summary ? setSummaryConfiguration : setConfiguration,
      configObject: options.summary ? summaryConfiguration : configuration,
    };
  }, [configuration, setConfiguration, setSummaryConfiguration, summaryConfiguration]);

  const handleSummarySelectVisibility = useCallback((type) => (e) => {
    const titles = Object.values(TitlesTypes);
    if (type === titles[0]) {
      return;
    }
    const visibilityValue = e.target.checked ? 'visible' : '';
    const titleIndex = titles.findIndex((value) => value === type);
    const titlesToSet = e.target.checked
      ? // Si cochée, on affiche tous les précédents
      titles.slice(0, titleIndex + 1)
      : // Si décochée on dés-affiche tous les suivants
      titles.slice(titleIndex, titles.length);
    // On affecte les titres à changer
    const newTitles = {};
    titlesToSet.forEach((title) => {
      newTitles[title] = {
        ...summaryConfiguration[title],
        visibility: visibilityValue,
      };
    });
    setSummaryConfiguration({
      ...newTitles,
    });
  }, [setSummaryConfiguration, summaryConfiguration]);

  const handleSelectColor =
    useCallback((type, options = {}) =>
      (selectedColor) => {
        const change = styleRuleChangeObject(options);
        change.setFunction({
          [type]: {
            ...change.configObject[type],
            color: configuration.colors.find(
              (color) => color.label === selectedColor
            ),
          },
        });
      }, [configuration?.colors, styleRuleChangeObject]);

  const handleSelectStyle =
    useCallback((type, options = {}) =>
      (e) => {
        const change = styleRuleChangeObject(options);
        const current = change.configObject[type].fontStyle;
        const checked = e?.target?.checked ?? (current === '');
        change.setFunction({
          [type]: {
            ...change.configObject[type],
            fontStyle: checked ? 'italic' : '',
          },
        });
      }, [styleRuleChangeObject]);

  const handleSelectWeight =
    useCallback((type, options = {}) =>
      (e) => {
        const change = styleRuleChangeObject(options);
        const current = change.configObject[type].fontWeight;
        const checked = e?.target?.checked ?? (current === '');
        change.setFunction({
          [type]: {
            ...change.configObject[type],
            fontWeight: checked ? 'bold' : '',
          },
        });
      }, [styleRuleChangeObject]);

  const handleSelectDecoration =
    useCallback((type, options = {}) =>
      (e) => {
        const change = styleRuleChangeObject(options);
        const current = change.configObject[type].textDecoration;
        const checked = e?.target?.checked ?? (current === '');
        change.setFunction({
          [type]: {
            ...change.configObject[type],
            textDecoration: checked ? 'underline' : '',
          },
        });
      }, [styleRuleChangeObject]);

  const handleChangeFontSize =
    useCallback((type, options = {}) =>
      (value) => {
        const change = styleRuleChangeObject(options);
        change.setFunction({
          [type]: { ...change.configObject[type], fontSize: value },
        });
      }, [styleRuleChangeObject]);

  const handleChangeMarginTop =
    useCallback((type, options = {}) =>
      (value) => {
        const change = styleRuleChangeObject(options);
        change.setFunction({
          [type]: { ...change.configObject[type], marginTop: value },
        });
      }, [styleRuleChangeObject]);

  const handleChangeMarginBottom =
    useCallback((type, options = {}) =>
      (value) => {
        const change = styleRuleChangeObject(options);
        change.setFunction({
          [type]: { ...change.configObject[type], marginBottom: value },
        });
      }, [styleRuleChangeObject]);

  const handleChangeIndexStyleFromTitles = useCallback((e) => {
    setSummaryConfiguration({ indexStyleFromTitles: e.target.checked });
  }, [setSummaryConfiguration]);

  const handleChangeIndentation = useCallback((value) => {
    setSummaryConfiguration({ indentation: value ?? 0 });
  }, [setSummaryConfiguration]);

  const handleChangeShowPageNumber =
    useCallback((type) =>
      (e) => {
        setSummaryConfiguration({
          [type]: { ...summaryConfiguration[type], showPageNumber: e.target.checked },
        });
      }, [setSummaryConfiguration, summaryConfiguration]);

  const handleChangePageLeader =
    useCallback((type) =>
      (pageLeader) => {
        setSummaryConfiguration({
          [type]: { ...summaryConfiguration[type], pageLeader },
        });
      }, [setSummaryConfiguration, summaryConfiguration]);

  const handleChangeSkipLineAfter =
    useCallback((type) =>
      (e) => {
        setSummaryConfiguration({
          [type]: { ...summaryConfiguration[type], skipLineAfter: e.target.checked },
        });
      }, [setSummaryConfiguration, summaryConfiguration]);

  const tabsItems = useMemo(() => {
    return Object.values(TitlesTypes).map((titleType, index) => {
      return {
        label: `Titres de niveau ${titleType[5]}`,
        key: titleType,
        children: <Col span={24} key={`title-${titleType}`}>
          <TitleConfiguration
            key={`summary-${titleType}`}
            title={titleType}
            level={titleType[5]}
            styleRules={summaryConfiguration[titleType]}
            colors={summaryConfiguration.colors}
            onChangeVisibility={handleSummarySelectVisibility(titleType)}
            onChangeColor={handleSelectColor(titleType, {
              summary: true,
            })}
            onChangeFontStyle={handleSelectStyle(titleType, {
              summary: true,
            })}
            onChangeWeight={handleSelectWeight(titleType, {
              summary: true,
            })}
            onChangeTextDecoration={handleSelectDecoration(titleType, {
              summary: true,
            })}
            onChangeFontSize={handleChangeFontSize(titleType, {
              summary: true,
            })}
            onChangeMarginTop={handleChangeMarginTop(titleType, {
              summary: true,
            })}
            onChangeMarginBottom={handleChangeMarginBottom(titleType, {
              summary: true,
            })}
            onChangeSkipLineAfter={handleChangeSkipLineAfter(titleType)}
            onChangeShowPageNumber={handleChangeShowPageNumber(titleType)}
            onChangePageLeader={handleChangePageLeader(titleType)}
            onChangeBackgroundColor={null}
            onChangeBorderColor={null}
            onChangeBorderPlace={null}
            configuration={configuration}
            titleType={titleType}
            type='summary'
            summaryConfiguration={summaryConfiguration[titleType]}
          />
        </Col>,
      }
    }
    )
  }, [
    configuration,
    handleChangeFontSize,
    handleSelectColor,
    handleSelectDecoration,
    handleSelectStyle,
    handleSelectWeight,
    handleSummarySelectVisibility,
    handleChangeSkipLineAfter,
    handleChangeShowPageNumber,
    handleChangePageLeader,
    summaryConfiguration
  ])

  return (
    <div>
      {/* Réglage du document entier, pas d'un niveau : il se pose au-dessus
          des onglets par niveau. La taille et la police du numéro restent
          celles du sommaire, seul son habillage suit les titres. */}
      <Checkbox
        checked={!!summaryConfiguration.indexStyleFromTitles}
        onChange={handleChangeIndexStyleFromTitles}
      >
        Numéros au style de ceux des titres du document
      </Checkbox>
      <Form.Item {...modalFieldLayout} label="Retrait des sous-titres" style={{ marginTop: '2%' }}>
        <InputNumber
          value={summaryConfiguration.indentation ?? 15}
          onChange={handleChangeIndentation}
          min={0}
          max={100}
          addonAfter="px"
          style={{ width: 150 }}
        />
      </Form.Item>
      <Row gutter={[0, 24]} style={{ marginTop: '2%' }}>
        <Tabs
          style={{ width: "100%" }}
          items={tabsItems}
          destroyInactiveTabPane
        />
      </Row>
    </div>
  );
};
